import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { Award, Check, Clock, Copy, Gift, Sparkles, Ticket, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/ui/status-badge';
import { tempoDecorrido } from '@/lib/format';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';
import { useFeatureFlag } from '@/lib/use-feature-flag';
import { useRealtimeInvalidate } from '@/lib/use-realtime-invalidate';

// Cast: tabelas novas (regras_cupom_parceiro, parceiros_fidelidade,
// cupons_emitidos) ainda não em database.types.ts
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb = supabase as any;

/**
 * /fidelidade — carteira de pontos do cliente. Saldo gigante no topo
 * (Apple Wallet vibe), próximo cupom desbloqueável com progress conic,
 * cards de oferta com gradient por categoria, extrato minimalista.
 */

interface ExtratoItem {
  cod_origem:
    | 'LAVAGEM'
    | 'PAGAMENTO'
    | 'INDICACAO'
    | 'WINBACK'
    | 'BONUS_ADMIN'
    | 'RESGATE'
    | 'EXPIRACAO';
  qtd_pontos: number;
  des_descricao: string | null;
  dta_lancamento: string;
  dta_expiracao: string | null;
}

interface SaldoResposta {
  qtd_saldo: number;
  qtd_pontos_proximos_a_expirar: number;
  jsn_extrato: ExtratoItem[];
}

const RotuloOrigem: Record<ExtratoItem['cod_origem'], string> = {
  LAVAGEM: 'Lavagem',
  PAGAMENTO: 'Pagamento',
  INDICACAO: 'Indicação',
  WINBACK: 'Win-back',
  BONUS_ADMIN: 'Bônus admin',
  RESGATE: 'Resgate',
  EXPIRACAO: 'Expiracao',
};

/** Conta de 0 → saldo com easing premium. Tela renderiza valor final acessível
 * imediatamente (sr-only) pra screen readers e testes; o overlay visível anima. */
function SaldoAnimado({ valor }: { valor: number }) {
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => Math.round(v).toLocaleString('pt-BR'));
  useEffect(() => {
    const c = animate(mv, valor, { duration: 1.1, ease: [0.16, 1, 0.3, 1] });
    return c.stop;
  }, [valor, mv]);
  return (
    <span className="relative inline-block" aria-label={`${valor} pontos`}>
      <span className="sr-only">{valor}</span>
      <motion.span aria-hidden>{display}</motion.span>
    </span>
  );
}

export function FidelidadePage() {
  const { tenant } = useTenant();
  const ativo = useFeatureFlag('PROGRAMA_FIDELIDADE', { fallback: false });
  // Sprint 11: filtro do extrato unificado (Wash + Combustivel)
  const [filtroExtrato, setFiltroExtrato] = useState<'TODOS' | 'LAVAGEM' | 'COMBUSTIVEL'>('TODOS');

  const saldo = useQuery({
    queryKey: ['fidelidade-saldo'],
    queryFn: async (): Promise<SaldoResposta> => {
      const { data, error } = await supabase.rpc('fn_meu_saldo_fidelidade', {
        p_qtd_extrato: 20,
      });
      if (error) throw error;
      const row = (data as unknown as SaldoResposta[] | null)?.[0];
      return (
        row ?? {
          qtd_saldo: 0,
          qtd_pontos_proximos_a_expirar: 0,
          jsn_extrato: [],
        }
      );
    },
    enabled: ativo === true,
  });

  // Sprint 11: extrato filtrado por origem (Wash | Combustivel)
  const extratoFiltrado = (saldo.data?.jsn_extrato ?? []).filter((e) => {
    if (filtroExtrato === 'TODOS') return true;
    if (filtroExtrato === 'LAVAGEM') return e.cod_origem === 'LAVAGEM';
    if (filtroExtrato === 'COMBUSTIVEL') return e.cod_origem === 'PAGAMENTO';
    return true;
  });

  if (ativo === undefined) {
    return (
      <div className="space-y-3 p-4 pb-24">
        <Skeleton className="h-44 rounded-3xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  if (!ativo) {
    return (
      <div className="p-4">
        <EmptyState
          variant="hero"
          icon={Award}
          title="Programa de fidelidade ainda não está ativo"
          description="O lava-jato ainda não habilitou o programa de pontos. Volte aqui quando for anunciado."
        />
      </div>
    );
  }

  const saldoAtual = saldo.data?.qtd_saldo ?? 0;
  const aExpirar = saldo.data?.qtd_pontos_proximos_a_expirar ?? 0;

  return (
    <div className="relative min-h-screen overflow-hidden pb-24">
      {/* Mesh decorativo no fundo */}
      <div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -right-32 -top-24 h-72 w-72"
        style={{ opacity: 0.22 }}
      />
      <div
        aria-hidden
        className="octa-blob bg-octa-accent pointer-events-none absolute -left-24 top-1/3 h-56 w-56"
        style={{ opacity: 0.18, animationDelay: '-7s' }}
      />

      <div className="relative z-10 space-y-5 p-4">
        {/* Page header */}
        <motion.div
          className="octa-page-header"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <h1>Carteira de pontos</h1>
          <p>Acompanhe seu saldo, ganhe pontos e troque por cupons exclusivos.</p>
        </motion.div>

        {/* Saldo hero — wallet vibe */}
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          <Card variant="wallet" className="octa-shimmer relative overflow-hidden rounded-[28px]">
            {/* Mesh interno */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-40"
              style={{
                backgroundImage:
                  'radial-gradient(at 100% 0%, rgba(255,255,255,0.45) 0px, transparent 50%), radial-gradient(at 0% 100%, rgba(255,255,255,0.2) 0px, transparent 50%)',
              }}
            />
            <CardContent className="relative space-y-5 p-7 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/85">
                  <Sparkles className="h-3.5 w-3.5" />
                  Seu saldo
                </div>
                {aExpirar > 0 && (
                  <span className="flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-semibold backdrop-blur-sm">
                    <Clock className="h-3 w-3" />
                    <span className="sr-only">{`${aExpirar} pts`}</span>
                    <span aria-hidden>{aExpirar} expiram em 30d</span>
                  </span>
                )}
              </div>

              <div className="relative flex items-end gap-2">
                {/* Screen-reader friendly + test-friendly: full text "{n} pts" */}
                <span className="sr-only">{saldo.isLoading ? '—' : `${saldoAtual} pts`}</span>
                <span
                  aria-hidden
                  className="text-display-2xl font-extrabold tabular-nums leading-none tracking-tight drop-shadow"
                >
                  {saldo.isLoading ? '—' : <SaldoAnimado valor={saldoAtual} />}
                </span>
                <span
                  aria-hidden
                  className="mb-2 text-sm font-bold uppercase tracking-wider text-white/85"
                >
                  pts
                </span>
              </div>

              <div className="flex items-center justify-between border-t border-white/15 pt-4">
                <div className="text-xs text-white/80">
                  <p className="font-semibold uppercase tracking-wider">
                    {tenant?.nom_tenant ?? 'Octa Pontos'}
                  </p>
                  <p className="mt-0.5 text-white/60">Programa de fidelidade</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 ring-2 ring-white/40 backdrop-blur-sm">
                  <Award className="h-5 w-5 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Cupons disponíveis */}
        <CuponsSection saldoAtual={saldoAtual} />

        {/* Extrato minimal */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="octa-section-title flex items-center gap-1.5">
              <TrendingUp className="h-3.5 w-3.5" />
              Últimos lançamentos
            </h2>
            {/* Sprint 11 — filtro Todos | Lavagem | Combustível */}
            <div className="border-border/60 bg-card inline-flex gap-0.5 rounded-full border p-0.5 text-[11px] font-semibold">
              {(['TODOS', 'LAVAGEM', 'COMBUSTIVEL'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFiltroExtrato(f)}
                  aria-pressed={filtroExtrato === f}
                  className={`rounded-full px-2.5 py-1 transition-colors ${
                    filtroExtrato === f
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {f === 'TODOS' ? 'Todos' : f === 'LAVAGEM' ? 'Lavagem' : 'Combustível'}
                </button>
              ))}
            </div>
          </div>
          <Card variant="flat" className="overflow-hidden">
            <CardContent className="p-0">
              {saldo.isLoading ? (
                <div className="space-y-2 p-4">
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
                  <Skeleton className="h-10" />
                </div>
              ) : saldo.error ? (
                <p className="p-4 text-sm text-rose-600">Erro: {(saldo.error as Error).message}</p>
              ) : extratoFiltrado.length === 0 ? (
                <p className="text-muted-foreground p-6 text-center text-sm">
                  {filtroExtrato === 'TODOS'
                    ? 'Você ainda não tem lançamentos. Lave seu carro pra começar a acumular!'
                    : `Nenhum lançamento em ${filtroExtrato === 'LAVAGEM' ? 'Lavagem' : 'Combustível'} ainda.`}
                </p>
              ) : (
                <ul className="divide-border/50 divide-y">
                  {extratoFiltrado.map((e, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + i * 0.04, duration: 0.3 }}
                      className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">{RotuloOrigem[e.cod_origem]}</p>
                        {e.des_descricao && (
                          <p className="text-muted-foreground truncate text-xs">
                            {e.des_descricao}
                          </p>
                        )}
                        <p className="text-muted-foreground mt-0.5 text-[11px]">
                          {tempoDecorrido(e.dta_lancamento)}
                        </p>
                      </div>
                      <p
                        className={`whitespace-nowrap text-base font-bold tabular-nums ${
                          e.qtd_pontos > 0 ? 'text-emerald-600' : 'text-rose-600'
                        }`}
                      >
                        {e.qtd_pontos > 0 ? '+' : ''}
                        {e.qtd_pontos} pts
                      </p>
                    </motion.li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

// =============================================================================
// CUPONS — secao de resgate + meus cupons emitidos
// =============================================================================

interface RegraCupom {
  seq_regra_cupom: number;
  seq_parceiro: number;
  nom_regra: string;
  des_regra: string | null;
  qtd_pontos_necessarios: number;
  cod_tipo_desconto: 'PERCENTUAL' | 'VALOR_FIXO' | 'LIVRE';
  val_desconto: number | null;
  qtd_dias_validade: number;
  des_observacoes: string | null;
  tab_parceiros_fidelidade?: {
    nom_parceiro: string;
    cod_categoria: string | null;
    des_logo_url: string | null;
  };
}

interface MeuCupom {
  seq_cupom_emitido: number;
  cod_cupom: string;
  cod_status: 'EMITIDO' | 'UTILIZADO' | 'EXPIRADO' | 'CANCELADO';
  qtd_pontos_debitados: number;
  dta_emissao: string;
  dta_expiracao: string;
  dta_utilizacao: string | null;
  tab_regras_cupom_parceiro?: {
    nom_regra: string;
    cod_tipo_desconto: string;
    val_desconto: number | null;
    tab_parceiros_fidelidade?: { nom_parceiro: string };
  };
}

const CATEGORIA_GRADIENT: Record<string, string> = {
  // Sem amber em nenhuma categoria — coral/laranja pra alimentação manda a mesma vibe quente
  ALIMENTACAO: 'from-orange-400 via-rose-500 to-pink-600',
  AUTOMOTIVO: 'from-sky-500 via-blue-600 to-indigo-700',
  BELEZA: 'from-pink-400 via-fuchsia-500 to-purple-600',
  SAUDE: 'from-emerald-400 via-teal-500 to-cyan-600',
  LAZER: 'from-violet-500 via-purple-600 to-fuchsia-700',
  DEFAULT: 'from-slate-600 via-slate-700 to-slate-900',
};

function CuponsSection({ saldoAtual }: { saldoAtual: number }) {
  const queryClient = useQueryClient();

  const ofertas = useQuery({
    queryKey: ['cupons-disponiveis'],
    queryFn: async () => {
      const { data, error } = await sb
        .from('tab_regras_cupom_parceiro')
        .select(
          'seq_regra_cupom, seq_parceiro, nom_regra, des_regra, qtd_pontos_necessarios, cod_tipo_desconto, val_desconto, qtd_dias_validade, des_observacoes, tab_parceiros_fidelidade(nom_parceiro, cod_categoria, des_logo_url)',
        )
        .eq('ind_ativo', true)
        .order('qtd_pontos_necessarios');
      if (error) throw error;
      return (data ?? []) as RegraCupom[];
    },
  });
  useRealtimeInvalidate('tab_regras_cupom_parceiro', ['cupons-disponiveis']);

  const meusCupons = useQuery({
    queryKey: ['meus-cupons'],
    queryFn: async () => {
      const { data, error } = await sb
        .from('tab_cupons_emitidos')
        .select(
          'seq_cupom_emitido, cod_cupom, cod_status, qtd_pontos_debitados, dta_emissao, dta_expiracao, dta_utilizacao, tab_regras_cupom_parceiro(nom_regra, cod_tipo_desconto, val_desconto, tab_parceiros_fidelidade(nom_parceiro))',
        )
        .order('dta_emissao', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as MeuCupom[];
    },
  });
  useRealtimeInvalidate('tab_cupons_emitidos', ['meus-cupons']);
  useRealtimeInvalidate('tab_cupons_emitidos', ['cupons-disponiveis']);

  const [novosCupons, setNovosCupons] = useState<number>(0);
  const resgatar = useMutation({
    mutationFn: async (seqRegra: number) => {
      const { data, error } = await sb.rpc('fn_emitir_cupom', { p_seq_regra: seqRegra });
      if (error) throw error;
      const row = (data as Array<{ cod_cupom: string; qtd_saldo_apos: number }>)?.[0];
      return row;
    },
    onSuccess: (row) => {
      if (row) {
        toast.success(`Cupom emitido: ${row.cod_cupom}`, {
          description: `Saldo após: ${row.qtd_saldo_apos} pts. Apresente o código no parceiro.`,
        });
        setNovosCupons((n) => n + 1);
        setTimeout(() => setNovosCupons(0), 3500);
        void queryClient.invalidateQueries({ queryKey: ['fidelidade-saldo'] });
        void queryClient.invalidateQueries({ queryKey: ['meus-cupons'] });
      }
    },
    onError: (e: Error) => toast.error('Falha', { description: traduzirErroSupabase(e) }),
  });

  const [copiado, setCopiado] = useState<string | null>(null);
  function copiar(codigo: string) {
    void navigator.clipboard.writeText(codigo);
    setCopiado(codigo);
    setTimeout(() => setCopiado(null), 2000);
  }

  // Próximo cupom desbloqueável (menos pontos necessários ≥ saldoAtual)
  const proximoDesbloqueavel = ofertas.data
    ?.filter((o) => o.qtd_pontos_necessarios > saldoAtual)
    .sort((a, b) => a.qtd_pontos_necessarios - b.qtd_pontos_necessarios)[0];
  const progresso = proximoDesbloqueavel
    ? Math.min((saldoAtual / proximoDesbloqueavel.qtd_pontos_necessarios) * 100, 100)
    : 100;

  return (
    <div className="space-y-5">
      {/* Celebração quando emite cupom */}
      <AnimatePresence>
        {novosCupons > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -8 }}
            transition={{ type: 'spring', stiffness: 280, damping: 18 }}
          >
            <Card
              variant="default"
              className="border-emerald-400/60 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40"
            >
              <CardContent className="flex items-center gap-3 p-4">
                <motion.div
                  animate={{ rotate: [0, -10, 10, -10, 0] }}
                  transition={{ duration: 0.6 }}
                  className="shadow-octa flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 text-white"
                >
                  <Gift className="h-5 w-5" />
                </motion.div>
                <div className="flex-1">
                  <p className="text-sm font-bold text-emerald-900 dark:text-emerald-100">
                    {novosCupons} cupom novo desbloqueado!
                  </p>
                  <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
                    Aproveita rápido — confira em &ldquo;Meus cupons&rdquo; embaixo.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Próximo desbloqueável — ring conic */}
      {proximoDesbloqueavel && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
        >
          <Card variant="default" className="overflow-hidden">
            <CardContent className="flex items-center gap-4 p-4">
              <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
                {/* Ring de progresso conic */}
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: `conic-gradient(hsl(var(--primary)) ${progresso * 3.6}deg, hsl(var(--muted)) 0deg)`,
                  }}
                />
                <div className="bg-card shadow-octa-sm relative flex h-12 w-12 items-center justify-center rounded-full">
                  <Ticket className="text-primary h-5 w-5" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-overline text-muted-foreground">Próximo cupom</p>
                <p className="truncate text-sm font-bold">{proximoDesbloqueavel.nom_regra}</p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  Faltam{' '}
                  <span className="text-foreground font-bold">
                    {proximoDesbloqueavel.qtd_pontos_necessarios - saldoAtual} pts
                  </span>{' '}
                  pra desbloquear
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Cupons disponíveis */}
      <div className="space-y-3">
        <h2 className="octa-section-title">Disponíveis para resgate</h2>
        {ofertas.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-28 rounded-2xl" />
            <Skeleton className="h-28 rounded-2xl" />
          </div>
        ) : !ofertas.data || ofertas.data.length === 0 ? (
          <EmptyState
            icon={Ticket}
            title="Sem ofertas no momento"
            description="Volte logo — novos parceiros entram toda semana."
          />
        ) : (
          <ul className="space-y-3">
            {ofertas.data.map((o, idx) => {
              const podeResgatar = saldoAtual >= o.qtd_pontos_necessarios;
              const cat = (o.tab_parceiros_fidelidade?.cod_categoria ?? 'DEFAULT').toUpperCase();
              const grad = CATEGORIA_GRADIENT[cat] ?? CATEGORIA_GRADIENT.DEFAULT;
              return (
                <motion.li
                  key={o.seq_regra_cupom}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + idx * 0.06, duration: 0.45 }}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.985 }}
                >
                  <Card variant="default" className="octa-card-tap overflow-hidden">
                    {/* Faixa colorida superior por categoria */}
                    <div className={`h-1.5 bg-gradient-to-r ${grad}`} aria-hidden />
                    <CardContent className="space-y-3 p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${grad} text-white shadow-md`}
                        >
                          {o.tab_parceiros_fidelidade?.des_logo_url ? (
                            <img
                              src={o.tab_parceiros_fidelidade.des_logo_url}
                              alt={o.tab_parceiros_fidelidade.nom_parceiro}
                              className="h-10 w-10 rounded-xl object-cover"
                            />
                          ) : (
                            <Ticket className="h-6 w-6" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-base font-bold tracking-tight">
                            {o.nom_regra}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {o.tab_parceiros_fidelidade?.nom_parceiro}
                            {o.tab_parceiros_fidelidade?.cod_categoria &&
                              ` · ${o.tab_parceiros_fidelidade.cod_categoria}`}
                          </p>
                        </div>
                        <span className="text-primary bg-primary/10 shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider">
                          {o.qtd_pontos_necessarios} pts
                        </span>
                      </div>
                      {o.des_regra && (
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          {o.des_regra}
                        </p>
                      )}
                      <p className="text-sm">
                        <strong>
                          {o.cod_tipo_desconto === 'PERCENTUAL' && `${o.val_desconto}% off`}
                          {o.cod_tipo_desconto === 'VALOR_FIXO' &&
                            `R$ ${Number(o.val_desconto).toFixed(2)} off`}
                          {o.cod_tipo_desconto === 'LIVRE' && o.des_observacoes}
                        </strong>{' '}
                        <span className="text-muted-foreground text-xs">
                          · válido {o.qtd_dias_validade}d após resgate
                        </span>
                      </p>
                      <Button
                        size="lg"
                        onClick={() => resgatar.mutate(o.seq_regra_cupom)}
                        disabled={!podeResgatar || resgatar.isPending}
                        className={`w-full ${
                          podeResgatar
                            ? 'bg-octa-gradient shadow-octa-glow text-white'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {podeResgatar
                          ? `Resgatar (${o.qtd_pontos_necessarios} pts)`
                          : `Faltam ${o.qtd_pontos_necessarios - saldoAtual} pts`}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Meus cupons */}
      <div className="space-y-3">
        <h2 className="octa-section-title">Meus cupons</h2>
        {meusCupons.isLoading ? (
          <Skeleton className="h-24 rounded-2xl" />
        ) : !meusCupons.data || meusCupons.data.length === 0 ? (
          <Card variant="flat">
            <CardContent className="p-4">
              <p className="text-muted-foreground text-sm">Você ainda não resgatou nenhum cupom.</p>
            </CardContent>
          </Card>
        ) : (
          <ul className="space-y-2">
            <AnimatePresence>
              {meusCupons.data.map((c, idx) => (
                <motion.li
                  key={c.seq_cupom_emitido}
                  layout
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ delay: idx * 0.04, duration: 0.35 }}
                >
                  <Card variant="default">
                    <CardContent className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold">
                          {c.tab_regras_cupom_parceiro?.tab_parceiros_fidelidade?.nom_parceiro} ·{' '}
                          {c.tab_regras_cupom_parceiro?.nom_regra}
                        </p>
                        <p className="text-base font-extrabold tracking-wider">{c.cod_cupom}</p>
                        <p className="text-muted-foreground text-[11px]">
                          Emitido {tempoDecorrido(c.dta_emissao)} · Expira{' '}
                          {new Date(c.dta_expiracao).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1.5">
                        <StatusBadge
                          dot
                          variant={
                            c.cod_status === 'EMITIDO'
                              ? 'success'
                              : c.cod_status === 'UTILIZADO'
                                ? 'neutral'
                                : 'warning'
                          }
                        >
                          {c.cod_status}
                        </StatusBadge>
                        {c.cod_status === 'EMITIDO' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copiar(c.cod_cupom)}
                            className="h-7 px-2"
                          >
                            {copiado === c.cod_cupom ? (
                              <>
                                <Check className="mr-1 h-3 w-3" />
                                Copiado
                              </>
                            ) : (
                              <>
                                <Copy className="mr-1 h-3 w-3" />
                                Copiar
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
      </div>
    </div>
  );
}
